import { useState, useEffect } from "react";
import { getEmployeeById, updateEmployee } from "../../services/EmployeeService";
import { getJobPositions, getDepartments, getEmploymentStatuses } from "../../services/LookupService";
import Button from "../CommonComponents/Button";
import Headline from "../MainComponents/Headline";
import { Card, Label, Input, Message } from "../CommonComponents/DataCard";
import PdfModal from "../BookComponents/PdfModal";
import pdfLogo from "../../assets/pdfRed.png"
import { PdfLink } from "../CommonComponents/Components";
import { ROLES } from "../../services/Constants";
import DatePicker from "../CommonComponents/DatePicker";

const modalSelectStyle = {
    width: "100%",
    padding: "0.5rem",
    marginBottom: "1rem",
    fontSize: "1rem",
    borderRadius: "4px",
    border: "1px solid #ccc",
};

function EmployeeProfileModal({ employeeId, onClose }) {
    const [profile, setProfile] = useState(null);
    const [editData, setEditData] = useState({});
    const [editing, setEditing] = useState(false);
    const [error, setError] = useState(false);
    const [message, setMessage] = useState(null);
    const [contractFile, setContractFile] = useState(null);
    const [showPdf, setShowPdf] = useState(false);
    const [jobPositions, setJobPositions] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [employmentStatuses, setEmploymentStatuses] = useState([]);

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await getEmployeeById(employeeId);
                setProfile(data);
                setEditData(data);
            } catch (err) {
                setError(true);
                setMessage("Greška kod dohvaćanja zaposlenika");
            }
        };
        fetchData();
    }, [employeeId]);

    useEffect(() => {
        async function loadLookups() {
            try {
                const [jp, d, es] = await Promise.all([
                    getJobPositions(), getDepartments(), getEmploymentStatuses()
                ]);
                setJobPositions(jp);
                setDepartments(d);
                setEmploymentStatuses(es);
            } catch (e) {
                console.error("Greška pri učitavanju šifarnika", e);
            }
        }
        loadLookups();
    }, []);

    const handleChange = (e) => {
        setMessage(null);
        setEditData({ ...editData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        setContractFile(e.target.files[0]);
    };

    const handleSave = async () => {
        try {
            const updated = await updateEmployee(employeeId, editData, contractFile);
            setProfile(updated);
            setEditData(updated);
            setEditing(false);
            setContractFile(null);
            setMessage("Podaci uspješno spremljeni");
        } catch (err) {
            setError(true);
            setMessage(err.message);
        }
    };

    const handleCancel = () => {
        setEditData(profile);
        setEditing(false);
        setError(false);
        setMessage(null);
        setContractFile(null);
    };

    if (!profile) return <p>Učitavanje...</p>;

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
            }}
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{ maxHeight: "100%", overflowY: "auto" }}
            >
                <Card>
                    <div style={{ padding: "2rem" }}>
                        <Headline size="2rem" center>Profil zaposlenika</Headline>

                        {!editing ? (
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <p><strong>Ime:</strong> {profile.name}</p>
                                <p><strong>Prezime:</strong> {profile.surname}</p>
                                <p><strong>Email:</strong> {profile.email}</p>
                                <p><strong>IBAN:</strong> {profile.iban}</p>
                                <p><strong>Datum rođenja:</strong> {profile.dateOfBirth}</p>
                                <p><strong>Državljanstvo:</strong> {profile.citizenship}</p>
                                <p><strong>Mjesto rođenja:</strong> {profile.placeOfBirth}</p>
                                <p><strong>Datum zaposlenja:</strong> {profile.dateOfEmployment}</p>
                                <p><strong>Pozicija:</strong> {profile.jobPosition}</p>
                                <p><strong>Odjel:</strong> {profile.department}</p>
                                <p><strong>Status zaposlenja:</strong> {profile.employmentStatus}</p>
                                <p><strong>Plaća:</strong> {profile.salary}</p>
                                <p><strong>Spol:</strong> {profile.gender === "MALE" ? "Muški" : "Ženski"}</p>
                                <p><strong>Uloga:</strong> {profile.role === ROLES.ADMIN ? "Administrator" : "Zaposlenik"}</p>
                                <PdfLink onClick={() => setShowPdf(true)}>
                                    <img src={pdfLogo} alt="PDF" width="24" height="24" />
                                    <p>Ugovor</p>
                                </PdfLink>

                                <div style={{ marginTop: "1rem", display: "flex", gap: "1rem" }}>
                                    <Button tekst="Uredi" akcija={() => setEditing(true)} />
                                    <Button tekst="Zatvori" akcija={onClose} />
                                </div>
                            </div>
                        ) : (
                            <form
                                style={{ display: "flex", flexDirection: "column" }}
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSave();
                                }}
                            >
                                <Label>Ime</Label>
                                <Input name="name" value={editData.name || ""} onChange={handleChange} />

                                <Label>Prezime</Label>
                                <Input name="surname" value={editData.surname || ""} onChange={handleChange} />

                                <Label>Email</Label>
                                <Input type="email" name="email" value={editData.email || ""} onChange={handleChange} />

                                <Label>IBAN</Label>
                                <Input name="iban" value={editData.iban || ""} onChange={handleChange} />

                                <Label>Datum rođenja</Label>
                                <DatePicker name="dateOfBirth" value={editData.dateOfBirth || ""} onChange={handleChange} maxYear={new Date().getFullYear() - 18} />

                                <Label>Državljanstvo</Label>
                                <Input name="citizenship" value={editData.citizenship || ""} onChange={handleChange} />

                                <Label>Mjesto rođenja</Label>
                                <Input name="placeOfBirth" value={editData.placeOfBirth || ""} onChange={handleChange} />

                                <Label>Datum zaposlenja</Label>
                                <DatePicker name="dateOfEmployment" value={editData.dateOfEmployment || ""} onChange={handleChange} minYear={1950} />

                                <Label>Radna pozicija</Label>
                                <select
                                    name="jobPositionId"
                                    value={editData.jobPositionId || ""}
                                    onChange={handleChange}
                                    style={modalSelectStyle}
                                >
                                    <option value="">Odaberite poziciju...</option>
                                    {jobPositions.map(jp => (
                                        <option key={jp.id} value={jp.id}>{jp.name}</option>
                                    ))}
                                </select>

                                <Label>Odjel</Label>
                                <select
                                    name="departmentId"
                                    value={editData.departmentId || ""}
                                    onChange={handleChange}
                                    style={modalSelectStyle}
                                >
                                    <option value="">Odaberite odjel...</option>
                                    {departments.map(d => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </select>

                                <Label>Status ugovora</Label>
                                <select
                                    name="employmentStatusId"
                                    value={editData.employmentStatusId || ""}
                                    onChange={handleChange}
                                    style={modalSelectStyle}
                                >
                                    <option value="">Odaberite status ugovora...</option>
                                    {employmentStatuses.map(es => (
                                        <option key={es.id} value={es.id}>{es.name}</option>
                                    ))}
                                </select>

                                <Label>Plaća</Label>
                                <Input type="number" name="salary" value={editData.salary || ""} onChange={handleChange} />

                                <Label>Spol</Label>
                                <div>
                                    <label>
                                        <input
                                            type="radio"
                                            name="gender"
                                            value="MALE"
                                            checked={editData.gender === "MALE"}
                                            onChange={handleChange}
                                        />
                                        Muški
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="gender"
                                            value="FEMALE"
                                            checked={editData.gender === "FEMALE"}
                                            onChange={handleChange}
                                        />
                                        Ženski
                                    </label>
                                </div>

                                <Label>Uloga</Label>
                                <select
                                    name="role"
                                    value={editData.role || ROLES.EMPLOYEE}
                                    onChange={handleChange}
                                    style={{
                                        width: "100%",
                                        padding: "0.5rem",
                                        marginBottom: "1rem",
                                        fontSize: "1rem",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                    }}
                                >
                                    <option value={ROLES.EMPLOYEE}>Zaposlenik</option>
                                    <option value={ROLES.ADMIN}>Administrator</option>
                                </select>

                                <Label>Ugovor (opcionalno)</Label>
                                <Input type="file" onChange={handleFileChange} />

                                {message && <Message $success={!error}>{message}</Message>}

                                <div style={{ marginTop: "1rem", display: "flex", gap: "1rem" }}>
                                    <Button type="submit" tekst="Spremi" />
                                    <Button type="button" tekst="Odustani" akcija={handleCancel} />
                                </div>
                            </form>
                        )}
                    </div>
                </Card>
                {showPdf && <PdfModal content={profile.employmentContract} onClose={() => setShowPdf(false)} />}

            </div>
        </div>
    );
}

export default EmployeeProfileModal;
